import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, MapPin, Users, AlertCircle, Check, X, Clock, Loader2,
  User, Home, CreditCard, Phone, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const statusConfig = {
  pending: {
    badge: 'bg-amber-100 text-amber-700',
    icon: Clock,
    label: 'Pending',
  },
  confirmed: {
    badge: 'bg-emerald-100 text-emerald-700',
    icon: Check,
    label: 'Confirmed',
  },
  rejected: {
    badge: 'bg-red-100 text-red-700',
    icon: X,
    label: 'Rejected',
  },
  cancelled: {
    badge: 'bg-slate-100 text-slate-700',
    icon: X,
    label: 'Cancelled',
  },
};

export default function Bookings() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isOwner } = useAuth();

  const [myBookings, setMyBookings] = useState([]);
  const [ownerBookings, setOwnerBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchBookings = async () => {
      try {
        // Fetch renter's bookings
        const myRes = await fetch(`${API_URL}/api/bookings`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        });

        if (myRes.ok) {
          const data = await myRes.json();
          setMyBookings(data.bookings || []);
        }

        // Fetch owner's booking requests if owner
        if (isOwner) {
          const ownerRes = await fetch(`${API_URL}/api/bookings/owner`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          });

          if (ownerRes.ok) {
            const data = await ownerRes.json();
            setOwnerBookings(data.bookings || []);
          }
        }
      } catch (error) {
        console.error('Failed to fetch bookings:', error);
        toast.error('Failed to load bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [isAuthenticated, isOwner, navigate]);

  const handleCancelBooking = async (bookingId) => {
    setActionLoading(bookingId);
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ status: 'cancelled' }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel booking');
      }

      setMyBookings(bookings =>
        bookings.map(b => b.id === bookingId ? { ...b, status: 'cancelled' } : b)
      );
      toast.success('Booking cancelled');
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateStatus = async (bookingId, status) => {
    setActionLoading(`${bookingId}-${status}`);
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update booking');
      }

      setOwnerBookings(bookings =>
        bookings.map(b => b.id === bookingId ? { ...b, status } : b)
      );
      toast.success(`Booking ${status}!`);
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const BookingCard = ({ booking, isOwnerView = false }) => {
    const StatusIcon = statusConfig[booking.status]?.icon || Clock;

    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="overflow-hidden hover:shadow-md transition-shadow">
          <CardContent className="p-6 space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">
                  {booking.property_title}
                </h3>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin className="h-4 w-4" />
                  {booking.property_city}
                </div>
              </div>
              <Badge className={statusConfig[booking.status]?.badge}>
                {statusConfig[booking.status]?.label}
              </Badge>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4 border-y border-slate-200 py-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Check-in</p>
                <p className="text-sm font-medium text-slate-900">
                  {new Date(booking.move_in_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Duration</p>
                <p className="text-sm font-medium text-slate-900">
                  {booking.duration} months
                </p>
              </div>
            </div>

            {/* User Info */}
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage
                  src={isOwnerView ? booking.renter_picture : booking.owner_picture}
                  alt={isOwnerView ? booking.renter_name : booking.owner_name}
                />
                <AvatarFallback>
                  {(isOwnerView ? booking.renter_name : booking.owner_name)?.split(' ')[0]?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 text-sm">
                  {isOwnerView ? booking.renter_name : booking.owner_name}
                </p>
                <div className="flex gap-2 text-xs text-slate-600 mt-1">
                  {(isOwnerView ? booking.renter_phone : booking.owner_phone) && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {isOwnerView ? booking.renter_phone : booking.owner_phone}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Message */}
            {booking.message && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs font-medium text-slate-600 mb-1">Message</p>
                <p className="text-sm text-slate-700">{booking.message}</p>
              </div>
            )}

            {/* Price */}
            <div className="pt-2 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <span className="text-slate-600 text-sm">Total Price</span>
                <span className="font-bold text-lg text-slate-900">
                  {booking.currency} {booking.total_price?.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              {!isOwnerView ? (
                <>
                  {booking.status === 'pending' && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="flex-1">
                          <X className="mr-2 h-4 w-4" />
                          Cancel
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel Booking?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to cancel this booking request? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogCancel>Keep Booking</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleCancelBooking(booking.id)}
                          disabled={actionLoading === booking.id}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          {actionLoading === booking.id ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Cancelling...
                            </>
                          ) : (
                            'Yes, Cancel'
                          )}
                        </AlertDialogAction>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => navigate(`/property/${booking.property_id}`)}
                  >
                    <Home className="mr-2 h-4 w-4" />
                    View Property
                  </Button>
                </>
              ) : (
                <>
                  {booking.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleUpdateStatus(booking.id, 'rejected')}
                        disabled={actionLoading === `${booking.id}-rejected`}
                      >
                        {actionLoading === `${booking.id}-rejected` ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <X className="mr-2 h-4 w-4" />
                        )}
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleUpdateStatus(booking.id, 'confirmed')}
                        disabled={actionLoading === `${booking.id}-confirmed`}
                      >
                        {actionLoading === `${booking.id}-confirmed` ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="mr-2 h-4 w-4" />
                        )}
                        Accept
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => navigate(`/property/${booking.property_id}`)}
                  >
                    <Home className="mr-2 h-4 w-4" />
                    View Property
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-6">
            <Skeleton className="h-12 w-48 mb-8" />
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-6 w-full mb-4" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h1 className="font-serif text-3xl md:text-4xl text-slate-900 mb-2">
              Bookings
            </h1>
            <p className="text-slate-600">Manage your booking requests and reservations</p>
          </motion.div>

          {/* Tabs */}
          <Tabs defaultValue="my-bookings" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="my-bookings">
                <Calendar className="h-4 w-4 mr-2" />
                My Bookings
              </TabsTrigger>
              {isOwner && (
                <TabsTrigger value="requests">
                  <Users className="h-4 w-4 mr-2" />
                  Requests
                </TabsTrigger>
              )}
            </TabsList>

            {/* My Bookings Tab */}
            <TabsContent value="my-bookings">
              {myBookings.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid gap-4">
                    <AnimatePresence>
                      {myBookings.map(booking => (
                        <BookingCard
                          key={booking.id}
                          booking={booking}
                          isOwnerView={false}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-semibold text-slate-900 mb-2">No Bookings Yet</h3>
                    <p className="text-slate-600 mb-6">
                      You haven't made any booking requests. Start by exploring properties!
                    </p>
                    <Button onClick={() => navigate('/search')}>
                      Browse Properties
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Booking Requests Tab (Owner) */}
            {isOwner && (
              <TabsContent value="requests">
                {ownerBookings.length > 0 ? (
                  <div className="space-y-4">
                    <div className="grid gap-4">
                      <AnimatePresence>
                        {ownerBookings.map(booking => (
                          <BookingCard
                            key={booking.id}
                            booking={booking}
                            isOwnerView={true}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="font-semibold text-slate-900 mb-2">No Booking Requests</h3>
                      <p className="text-slate-600">
                        You don't have any pending booking requests yet.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
}
